// Enhanced asset data with descriptions, relationships, and metadata
// Used for Connected Highlighting and Details Panel features

export const TRANSFORMER_DATA = [
  {
    id: "1",
    number: 1,
    name: "HV/LV/MV Bushings",
    color: "#3B82F6",
    failureModes: [
      {
        name: "Overheating",
        description: "Excessive temperature rise in bushing due to high current, poor connections, or internal defects.",
        importance: "Can lead to insulation breakdown, oil leakage, and catastrophic bushing failure. Early detection prevents transformer damage.",
        approach: "Identify root cause through thermal imaging and connection inspection. Check for overloading conditions.",
        severity: "Critical",
        relatedTests: ["Infrared Thermography", "Power Factor Test"],
        relatedMonitoring: ["Bushing Monitors", "Online PD Sensors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/7bcf7a401e70a9f20cf9215b1419beeb5b20b1cad11d19fb8b77e093a3c2fe69.png", caption: "Thermal Scan Showing Hot Spots" }
        ]
      },
      {
        name: "Insulation Aging",
        description: "Gradual degradation of bushing insulation material over time due to thermal, electrical, and environmental stresses.",
        importance: "Reduces dielectric strength and increases failure risk. Aging accelerates under high temperature and moisture conditions.",
        approach: "Monitor through periodic testing and trend analysis. Plan replacement based on condition assessment.",
        severity: "Moderate",
        relatedTests: ["Power Factor Test", "Tan Delta Test"],
        relatedMonitoring: ["Capacitance Trending"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/c3869e74af3ed6a77892a4ee37d5b69f5707b40765ae92de83eb7f6c914bf441.png", caption: "Bushing Insulation Degradation" }
        ]
      },
      {
        name: "Bad Tap Contact",
        description: "Poor electrical contact at bushing tap connections causing localized heating and resistance increase.",
        importance: "Creates hot spots that can damage surrounding insulation and lead to connection failure.",
        approach: "Inspect and retorque connections. Clean contact surfaces and apply appropriate compounds.",
        severity: "Moderate",
        relatedTests: ["Infrared Thermography", "Capacitance Measurement"],
        relatedMonitoring: ["Bushing Monitors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/7bcf7a401e70a9f20cf9215b1419beeb5b20b1cad11d19fb8b77e093a3c2fe69.png", caption: "Thermal Detection of Bad Contact" }
        ]
      },
      {
        name: "Short Circuit in Capacitive Layers",
        description: "Internal short between capacitive grading layers within condenser-type bushings.",
        importance: "Alters capacitance values and can lead to uneven voltage distribution, increasing failure risk.",
        approach: "Compare capacitance measurements to baseline. Replace bushing if significant deviation detected.",
        severity: "Critical",
        relatedTests: ["Capacitance Measurement", "Power Factor Test"],
        relatedMonitoring: ["Capacitance Trending"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/fd7731b46ef4243d1c6f673ed2ab31d0a29f0544c9e8805a319723f462f75353.png", caption: "Bushing Capacitive Layers Structure" }
        ]
      },
      {
        name: "Moisture Ingress",
        description: "Water penetration into bushing through damaged seals, cracks, or gasket failures.",
        importance: "Moisture reduces insulation strength dramatically and accelerates degradation. Can cause flashover.",
        approach: "Inspect seals and gaskets. Check oil for moisture content. Dry or replace affected components.",
        severity: "Critical",
        relatedTests: ["Power Factor Test", "Tan Delta Test"],
        relatedMonitoring: ["Bushing Monitors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/c3869e74af3ed6a77892a4ee37d5b69f5707b40765ae92de83eb7f6c914bf441.png", caption: "Bushing Damage from Moisture" }
        ]
      },
      {
        name: "Partial Discharge",
        description: "Localized electrical discharges that do not completely bridge the insulation gap.",
        importance: "Indicates insulation degradation and can progressively damage surrounding material, leading to failure.",
        approach: "Locate PD source using acoustic or electrical methods. Assess severity and plan intervention.",
        severity: "Critical",
        relatedTests: ["Power Factor Test", "Tan Delta Test"],
        relatedMonitoring: ["Online PD Sensors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/9c3ef80b5950704fbeda8c64fb157b724733ac0d589c86d886e9c007b4ef7d21.png", caption: "PD Sensor Installation" }
        ]
      }
    ],
    tests: [
      {
        name: "Power Factor Test",
        description: "Measures dielectric losses in bushing insulation by comparing real power to apparent power.",
        importance: "Detects moisture, contamination, and aging in insulation. Trending reveals degradation patterns.",
        approach: "Apply test voltage and measure power factor/dissipation factor. Compare to nameplate and historical values.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Insulation Aging", "Moisture Ingress", "Partial Discharge"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/aac881654c6bb45937545b0d0b249b4c65cd821ec56bc6d525495e0763075eec.png", caption: "Power Factor Test Equipment" }
        ]
      },
      {
        name: "Capacitance Measurement",
        description: "Measures the capacitance of bushing layers to detect internal shorts or open circuits.",
        importance: "Changes in capacitance indicate layer failures. Critical for condenser-type bushings.",
        approach: "Measure C1 and C2 capacitance. Compare to factory values and previous measurements.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Short Circuit in Capacitive Layers", "Bad Tap Contact"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/aac881654c6bb45937545b0d0b249b4c65cd821ec56bc6d525495e0763075eec.png", caption: "Capacitance Test Setup" }
        ]
      },
      {
        name: "Infrared Thermography",
        description: "Non-contact temperature measurement using thermal imaging cameras.",
        importance: "Identifies hot spots indicating poor connections, overloading, or internal defects before failure.",
        approach: "Scan bushing surfaces under load. Compare temperatures between phases and to ambient.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Overheating", "Bad Tap Contact"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/7bcf7a401e70a9f20cf9215b1419beeb5b20b1cad11d19fb8b77e093a3c2fe69.png", caption: "Thermal Imaging of Transformer" }
        ]
      },
      {
        name: "Tan Delta Test",
        description: "Measures the tangent of the loss angle in insulation, indicating dielectric losses.",
        importance: "More sensitive than power factor for detecting early-stage insulation degradation.",
        approach: "Apply test voltage at multiple frequencies. Analyze tip-up characteristics for moisture detection.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Insulation Aging", "Moisture Ingress", "Partial Discharge"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/2a46644ebd7998f09916d282c0a371fa3ec72905ccbfa04d299d5972d199e890.png", caption: "Tan Delta Test Equipment" }
        ]
      }
    ],
    monitoring: [
      {
        name: "Bushing Monitors",
        description: "Continuous online monitoring of bushing capacitance, power factor, and leakage current.",
        importance: "Provides real-time health status and early warning of developing faults.",
        approach: "Install sensors on bushing taps. Connect to SCADA for alerts and trending.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Overheating", "Bad Tap Contact", "Moisture Ingress"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/ac243ed77dd96fd18f21284e0fb65173cf7e0afef6feac2c87559ccfdd640742.png", caption: "Online Bushing Monitoring System" }
        ]
      },
      {
        name: "Online PD Sensors",
        description: "Continuous monitoring for partial discharge activity using UHF or acoustic sensors.",
        importance: "Early detection of PD allows planned intervention before insulation failure.",
        approach: "Install sensors near bushings. Set alarm thresholds based on baseline measurements.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Partial Discharge"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/9c3ef80b5950704fbeda8c64fb157b724733ac0d589c86d886e9c007b4ef7d21.png", caption: "PD Monitoring Sensors" }
        ]
      },
      {
        name: "Capacitance Trending",
        description: "Long-term tracking of bushing capacitance values to identify gradual changes.",
        importance: "Trend analysis reveals slow degradation that single measurements might miss.",
        approach: "Record measurements periodically. Plot trends and set deviation alarms.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Short Circuit in Capacitive Layers", "Insulation Aging"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/21ec270f5e812fba96bb901274c9bc2028f53b16b7b1e731272546b77a611b09.png", caption: "Capacitance Trending Chart" }
        ]
      }
    ]
  },
  {
    id: "2",
    number: 2,
    name: "Cooling System",
    color: "#10B981",
    failureModes: [
      {
        name: "Fan/Pump Failure",
        description: "Complete failure of cooling fans or oil circulation pumps due to motor burnout, bearing failure, or electrical issues.",
        importance: "Loss of forced cooling can rapidly increase transformer temperature, leading to accelerated aging or thermal failure.",
        approach: "Verify motor operation, check bearings, inspect electrical connections. Replace failed components.",
        severity: "Critical",
        relatedTests: ["Cooling Performance Test", "Fan Speed Verification"],
        relatedMonitoring: ["Motor Current Monitoring", "Vibration Monitors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/ff7a17a0e187d8c5de589237a64a251d071d875864b757968aa723c7b36ad02b.png", caption: "Cooling System Overview" }
        ]
      },
      {
        name: "Cooling Control Failure",
        description: "Malfunction of automatic cooling control systems including temperature sensors, relays, or control logic.",
        importance: "May prevent cooling stages from activating when needed, risking thermal runaway.",
        approach: "Test control system response. Verify sensor accuracy and relay operation.",
        severity: "Critical",
        relatedTests: ["Cooling Performance Test"],
        relatedMonitoring: ["Temperature Sensors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/558a66bfa52a5349f80f8bda20f3efea19356363cc707d45b349ca9b737074c8.png", caption: "Temperature and Flow Monitoring" }
        ]
      },
      {
        name: "Pump/Fan Running in Reverse",
        description: "Incorrect rotation direction after maintenance or due to phase reversal.",
        importance: "Significantly reduces cooling efficiency while appearing to operate normally.",
        approach: "Verify rotation direction during commissioning. Check phase sequence after any electrical work.",
        severity: "Moderate",
        relatedTests: ["Fan Speed Verification", "Pump Flow Rate Test"],
        relatedMonitoring: ["Flow Meters"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/28ac5ea2836bad537f1a1a778f4791bd527e000c3b5710cd5476c6dc9b5b2c30.png", caption: "Fan Rotation Direction Check" }
        ]
      },
      {
        name: "Radiator Blockages",
        description: "Restricted airflow or oil flow through radiators due to debris, insects, or internal sludge.",
        importance: "Reduces heat dissipation capacity, forcing transformer to operate at higher temperatures.",
        approach: "Clean radiator fins externally. Flush internal passages if sludge is suspected.",
        severity: "Moderate",
        relatedTests: ["Cooling Performance Test", "Thermal Imaging"],
        relatedMonitoring: ["Temperature Sensors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/9c532d1147e94e2eaf0226414c7a02fad73023647adc5600271a7abf19bdd4ac.png", caption: "Blocked Radiator Fins" }
        ]
      },
      {
        name: "Reduced Efficiency",
        description: "Gradual decline in cooling system performance due to aging components or accumulated contamination.",
        importance: "Higher operating temperatures accelerate insulation aging, reducing transformer lifespan.",
        approach: "Benchmark against original design. Plan maintenance or upgrades as needed.",
        severity: "Moderate",
        relatedTests: ["Cooling Performance Test", "Thermal Imaging"],
        relatedMonitoring: ["Temperature Sensors", "Flow Meters"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/7bcf7a401e70a9f20cf9215b1419beeb5b20b1cad11d19fb8b77e093a3c2fe69.png", caption: "Thermal Analysis" }
        ]
      },
      {
        name: "Fan/Pump End of Life",
        description: "Components approaching or exceeding design service life, showing wear but not yet failed.",
        importance: "Proactive replacement prevents unexpected failures during critical periods.",
        approach: "Track operating hours. Plan replacement based on manufacturer recommendations and condition.",
        severity: "Low",
        relatedTests: ["Fan Speed Verification", "Pump Flow Rate Test"],
        relatedMonitoring: ["Vibration Monitors", "Motor Current Monitoring"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/575f8fdf883a39164ed97014fe239ff2c73ce7d618297d283d7c86cb224ac6ac.png", caption: "Vibration Monitoring" }
        ]
      }
    ],
    tests: [
      {
        name: "Cooling Performance Test",
        description: "Comprehensive test of cooling system capacity under various load conditions.",
        importance: "Verifies system can handle rated load. Identifies degradation before it affects transformer operation.",
        approach: "Load transformer to rated capacity. Monitor temperature rise and compare to design values.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Cooling Control Failure", "Radiator Blockages", "Reduced Efficiency"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/558a66bfa52a5349f80f8bda20f3efea19356363cc707d45b349ca9b737074c8.png", caption: "Temperature Monitoring Equipment" }
        ]
      },
      {
        name: "Pump Flow Rate Test",
        description: "Measures oil circulation rate through cooling system.",
        importance: "Confirms adequate oil flow for heat transfer. Detects pump degradation or blockages.",
        approach: "Install flow meter temporarily. Compare measured flow to design specification.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Fan/Pump Failure", "Pump/Fan Running in Reverse"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/558a66bfa52a5349f80f8bda20f3efea19356363cc707d45b349ca9b737074c8.png", caption: "Flow Measurement Gauges" }
        ]
      },
      {
        name: "Fan Speed Verification",
        description: "Measures actual fan rotation speed and compares to rated values.",
        importance: "Identifies motor issues, blade damage, or incorrect rotation before complete failure.",
        approach: "Use tachometer to measure RPM. Verify rotation direction. Check for unusual vibration.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Fan/Pump Failure", "Pump/Fan Running in Reverse", "Fan/Pump End of Life"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/ff7a17a0e187d8c5de589237a64a251d071d875864b757968aa723c7b36ad02b.png", caption: "Cooling Fan Inspection" }
        ]
      },
      {
        name: "Thermal Imaging",
        description: "Infrared survey of cooling system components under operating conditions.",
        importance: "Reveals hot spots, blocked passages, and uneven cooling distribution.",
        approach: "Scan radiators, pumps, and connections while system is operating. Compare phase temperatures.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Radiator Blockages", "Reduced Efficiency"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/7bcf7a401e70a9f20cf9215b1419beeb5b20b1cad11d19fb8b77e093a3c2fe69.png", caption: "Thermal Scan Results" }
        ]
      }
    ],
    monitoring: [
      {
        name: "Temperature Sensors",
        description: "Continuous monitoring of oil temperature, winding temperature, and ambient conditions.",
        importance: "Provides early warning of cooling issues. Enables automatic cooling stage control.",
        approach: "Install RTDs or thermocouples at key points. Connect to protection and SCADA systems.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Cooling Control Failure", "Radiator Blockages", "Reduced Efficiency"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/558a66bfa52a5349f80f8bda20f3efea19356363cc707d45b349ca9b737074c8.png", caption: "Temperature Gauges" }
        ]
      },
      {
        name: "Flow Meters",
        description: "Continuous measurement of oil flow rate through cooling circuits.",
        importance: "Detects pump degradation, blockages, or valve issues in real-time.",
        approach: "Install inline flow sensors. Set alarms for low flow conditions.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Pump/Fan Running in Reverse", "Reduced Efficiency"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/cc391dc60684b5e69de7db84cb2677e26e1c36a09a8350d5efeac5b504cff29b.png", caption: "Oil Flow Meter Installation" }
        ]
      },
      {
        name: "Vibration Monitors",
        description: "Continuous vibration analysis of rotating components like fans and pumps.",
        importance: "Detects bearing wear, imbalance, and misalignment before catastrophic failure.",
        approach: "Mount accelerometers on motor housings. Analyze frequency spectrum for fault signatures.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Fan/Pump Failure", "Fan/Pump End of Life"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/1b7e19620f4b2da1d8c5a3c16d078afbadbae4b2e5b7dd1496f9611fce221460.png", caption: "Vibration Sensor on Motor" }
        ]
      },
      {
        name: "Motor Current Monitoring",
        description: "Tracking of fan and pump motor current draw over time.",
        importance: "Current changes indicate motor health, mechanical loading, and developing faults.",
        approach: "Install current transformers. Trend data and set deviation alarms.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Fan/Pump Failure", "Fan/Pump End of Life"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/189b0a37e744d0c8660e8045f844524fb20875c77fbf20609c013870ebafd1bb.png", caption: "Motor Current Monitoring Panel" }
        ]
      }
    ]
  },
  {
    id: "3",
    number: 3,
    name: "Main Tank & Oil",
    color: "#F59E0B",
    failureModes: [
      {
        name: "Excess Moisture in Oil",
        description: "Water contamination in transformer oil from seal leaks, breathing system issues, or condensation.",
        importance: "Moisture dramatically reduces dielectric strength. Can cause flashover and accelerates insulation aging.",
        approach: "Dry oil through filtration or vacuum treatment. Identify and seal moisture ingress paths.",
        severity: "Critical",
        relatedTests: ["Oil Quality Tests", "Dissolved Gas Analysis (DGA)"],
        relatedMonitoring: ["Moisture Sensors", "Online DGA"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/c566323d42862dba0ec99bad2100da663f9ff38f9bb045aa32423727d3ca1261.png", caption: "Oil Moisture Testing Equipment" }
        ]
      },
      {
        name: "Loose Connections",
        description: "High-resistance connections at terminals, tap leads, or internal joints.",
        importance: "Creates hot spots generating gases. Can progress to arcing and catastrophic failure.",
        approach: "Identify through DGA (hot metal gases) and thermography. Retorque or repair connections.",
        severity: "Critical",
        relatedTests: ["Dissolved Gas Analysis (DGA)"],
        relatedMonitoring: ["Online DGA"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/354bed5396802a3f9a437d188da6b97825eb1eae78c9e28eb862c881709718d4.png", caption: "Damaged Loose Connection" }
        ]
      },
      {
        name: "PD in Oil Insulation",
        description: "Partial discharge activity occurring in oil-filled spaces due to contamination or design issues.",
        importance: "Generates hydrogen gas and degrades oil. Can progress to complete breakdown.",
        approach: "Locate PD source. Filter oil to remove contamination. Address design issues if needed.",
        severity: "Critical",
        relatedTests: ["Dissolved Gas Analysis (DGA)"],
        relatedMonitoring: ["Online DGA"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/b7b64994309463ebd5e38dc6f173e4253a718ddd8f8634036ddf59877ced609d.png", caption: "PD Detection Equipment" }
        ]
      },
      {
        name: "Oil Level Drops",
        description: "Decreasing oil level due to leaks, evaporation, or conservator system issues.",
        importance: "Low oil exposes insulation to air, causing accelerated degradation and potential flashover.",
        approach: "Locate and repair leaks. Check conservator bladder integrity. Top up with compatible oil.",
        severity: "Moderate",
        relatedTests: ["Pressure Tests"],
        relatedMonitoring: ["Oil Level Sensors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/ba08f83ff19f459d36b1af56257e49819ba1d88f6d62225925950ede7b9afc63.png", caption: "Oil Level Gauge Indicator" }
        ]
      },
      {
        name: "Sudden Rise in Tank Pressure",
        description: "Rapid pressure increase from internal fault, gas generation, or blocked pressure relief.",
        importance: "Can rupture tank if pressure relief fails. Often indicates severe internal fault.",
        approach: "Investigate cause immediately. Check pressure relief device operation. Perform DGA.",
        severity: "Critical",
        relatedTests: ["Dissolved Gas Analysis (DGA)", "Pressure Tests"],
        relatedMonitoring: ["Pressure Relief Monitors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/2fe4dfd10ef730023bfb6479855515401421efa63b4097da22ecb1e991f68e20.png", caption: "Pressure Relief Device Activation" }
        ]
      },
      {
        name: "Conservator Bladder Rupture",
        description: "Failure of the rubber bladder separating oil from air in the conservator.",
        importance: "Allows moisture and oxygen to contact oil, accelerating degradation.",
        approach: "Replace bladder. Check oil condition and dry if necessary.",
        severity: "Moderate",
        relatedTests: ["Oil Quality Tests"],
        relatedMonitoring: ["Moisture Sensors", "Oil Level Sensors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/42ffeb4c7734fb17791524dc3ae6b5f1455c0b7c772fe0ad4e7b915d9e82586d.png", caption: "Conservator Bladder Diagram" }
        ]
      }
    ],
    tests: [
      {
        name: "Dissolved Gas Analysis (DGA)",
        description: "Laboratory analysis of gases dissolved in transformer oil to detect internal faults.",
        importance: "Most important diagnostic for detecting developing faults. Different fault types produce characteristic gas patterns.",
        approach: "Extract oil sample. Analyze for H2, CH4, C2H2, C2H4, C2H6, CO, CO2. Interpret using Duval Triangle or Rogers Ratios.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Excess Moisture in Oil", "Loose Connections", "PD in Oil Insulation", "Sudden Rise in Tank Pressure"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/be7489bb9c27ef82c7d01f11b5dab47f3ba2a8b19a93dd92b082715525c3d3f6.png", caption: "Duval Triangle Analysis" },
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/147f2abd5ed8a45d7f949e86f9a976bff120c3283d1dc712a853ae93fb948752.png", caption: "DGA Gas Concentration Chart" },
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/f317fac610f027f223b15c181d3791a2cb66823afbfde0d0daf79c59bbe0f1da.png", caption: "Oil Sampling Equipment" }
        ]
      },
      {
        name: "Oil Quality Tests",
        description: "Comprehensive testing of oil physical and chemical properties including dielectric strength, acidity, and moisture.",
        importance: "Determines oil condition and remaining life. Guides maintenance decisions.",
        approach: "Perform breakdown voltage, water content, acidity, interfacial tension, and color tests.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Excess Moisture in Oil", "Conservator Bladder Rupture"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/f317fac610f027f223b15c181d3791a2cb66823afbfde0d0daf79c59bbe0f1da.png", caption: "Oil Sampling and Testing" }
        ]
      },
      {
        name: "Pressure Tests",
        description: "Testing of tank pressure integrity and pressure relief device operation.",
        importance: "Ensures tank can withstand internal pressures and relief devices function correctly.",
        approach: "Pressurize tank to test pressure. Verify relief device operation and reset.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Oil Level Drops", "Sudden Rise in Tank Pressure"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/5802cb86ae1037ca2e6af9bfb72ef75e4ad813e8a7fa1da2d67f408e5c7df975.png", caption: "Pressure Test Equipment" }
        ]
      },
      {
        name: "Furan Analysis",
        description: "Measurement of furanic compounds in oil indicating cellulose insulation degradation.",
        importance: "Only way to assess paper insulation condition without internal inspection. Critical for life assessment.",
        approach: "Extract oil sample. Measure 2-FAL and other furans by HPLC. Calculate estimated DP of paper.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Excess Moisture in Oil"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/65b83f52bac97c684e40698bcc3d3fcb85d4bc98e49823e45335ef17ff05d930.png", caption: "HPLC Furan Analysis Equipment" }
        ]
      }
    ],
    monitoring: [
      {
        name: "Online DGA",
        description: "Continuous or periodic automatic analysis of dissolved gases in oil.",
        importance: "Provides trend data and early warning of developing faults without manual sampling.",
        approach: "Install DGA monitor on main tank. Configure alarms for key gases.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Loose Connections", "PD in Oil Insulation", "Excess Moisture in Oil"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/147f2abd5ed8a45d7f949e86f9a976bff120c3283d1dc712a853ae93fb948752.png", caption: "Gas Concentration Trending" },
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/be7489bb9c27ef82c7d01f11b5dab47f3ba2a8b19a93dd92b082715525c3d3f6.png", caption: "Duval Triangle Fault Diagnosis" }
        ]
      },
      {
        name: "Oil Level Sensors",
        description: "Continuous monitoring of oil level in main tank and conservator.",
        importance: "Detects leaks and conservator issues early. Prevents low-oil damage.",
        approach: "Install float or magnetic level sensors. Set low-level alarms.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Oil Level Drops", "Conservator Bladder Rupture"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/ba08f83ff19f459d36b1af56257e49819ba1d88f6d62225925950ede7b9afc63.png", caption: "Oil Level Gauge" }
        ]
      },
      {
        name: "Pressure Relief Monitors",
        description: "Sensors detecting pressure relief device operation or trip status.",
        importance: "Alerts operators to overpressure events that may indicate internal faults.",
        approach: "Install position or flow sensors on relief devices. Connect to alarm system.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Sudden Rise in Tank Pressure"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/2fe4dfd10ef730023bfb6479855515401421efa63b4097da22ecb1e991f68e20.png", caption: "Pressure Relief Device" }
        ]
      },
      {
        name: "Moisture Sensors",
        description: "Online measurement of water content in transformer oil.",
        importance: "Tracks moisture ingress in real-time. Enables proactive drying before damage occurs.",
        approach: "Install capacitive or optical moisture sensors. Trend data and set moisture alarms.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Excess Moisture in Oil", "Conservator Bladder Rupture"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/c566323d42862dba0ec99bad2100da663f9ff38f9bb045aa32423727d3ca1261.png", caption: "Moisture Testing Equipment" }
        ]
      }
    ]
  },
  {
    id: "4",
    number: 4,
    name: "Winding & Magnetic Circuit",
    color: "#EF4444",
    failureModes: [
      {
        name: "Winding/Core Overheating",
        description: "Excessive temperature in windings or core due to overloading, poor cooling, or internal faults.",
        importance: "Accelerates insulation aging exponentially. Every 6-8°C above design cuts life in half.",
        approach: "Review loading history. Check cooling system. Inspect for blocked ducts or hot spots.",
        severity: "Critical",
        relatedTests: ["Winding Resistance", "SFRA (Sweep Frequency Response)"],
        relatedMonitoring: ["Fiber Optic Temp Sensors", "Winding Hot Spot Monitors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/7bcf7a401e70a9f20cf9215b1419beeb5b20b1cad11d19fb8b77e093a3c2fe69.png", caption: "Thermal Analysis of Windings" },
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/f979df3143ae6e2bb1d78cf12f74a6dbc4c0ebc5ffd4a5693a10fbb05b471ea1.png", caption: "Fiber Optic Temperature Monitoring" }
        ]
      },
      {
        name: "Excess Moisture in Insulation",
        description: "High water content in paper insulation reducing its dielectric and mechanical strength.",
        importance: "Wet insulation has dramatically lower breakdown voltage. Bubbling can occur at high loads.",
        approach: "Dry insulation through vacuum treatment or hot oil spray. Address moisture sources.",
        severity: "Critical",
        relatedTests: ["Insulation Resistance"],
        relatedMonitoring: ["Fiber Optic Temp Sensors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/f317fac610f027f223b15c181d3791a2cb66823afbfde0d0daf79c59bbe0f1da.png", caption: "Oil Moisture Testing" }
        ]
      },
      {
        name: "Generation of Bubbles",
        description: "Gas bubble formation in oil when hot spot temperatures exceed ~140°C or from rapid pressure changes.",
        importance: "Bubbles create low-dielectric paths that can cause flashover and catastrophic failure.",
        approach: "Reduce load immediately if detected. Investigate hot spots and address root cause.",
        severity: "Critical",
        relatedTests: ["SFRA (Sweep Frequency Response)", "Insulation Resistance"],
        relatedMonitoring: ["Winding Hot Spot Monitors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/be7489bb9c27ef82c7d01f11b5dab47f3ba2a8b19a93dd92b082715525c3d3f6.png", caption: "Gas Analysis - Duval Triangle" }
        ]
      },
      {
        name: "PD in Winding Insulation",
        description: "Partial discharge activity in winding insulation due to voids, contamination, or aging.",
        importance: "Progressive PD erodes insulation until complete failure. Often indicates design issues.",
        approach: "Locate PD source using electrical or acoustic methods. Plan repair or replacement.",
        severity: "Critical",
        relatedTests: ["SFRA (Sweep Frequency Response)"],
        relatedMonitoring: ["Online PD Monitoring"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/9c3ef80b5950704fbeda8c64fb157b724733ac0d589c86d886e9c007b4ef7d21.png", caption: "PD Sensor Installation" }
        ]
      },
      {
        name: "Loose Connection with Bushings",
        description: "High-resistance connections between windings and bushing leads.",
        importance: "Creates hot spots visible in DGA. Can progress to open circuit or arcing.",
        approach: "Retorque connections if accessible. May require internal inspection.",
        severity: "Critical",
        relatedTests: ["Winding Resistance"],
        relatedMonitoring: ["Fiber Optic Temp Sensors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/7bcf7a401e70a9f20cf9215b1419beeb5b20b1cad11d19fb8b77e093a3c2fe69.png", caption: "Connection Hot Spot Detection" }
        ]
      },
      {
        name: "Loss of Core Ground",
        description: "Open circuit in the single-point core grounding system.",
        importance: "Allows core to float at unknown potential. Can cause arcing and damage.",
        approach: "Verify core ground continuity. Repair open connection.",
        severity: "Moderate",
        relatedTests: ["Core Ground Test"],
        relatedMonitoring: [],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/804e3d5a625b41094201376bbf63f37f8253ed459d2b9c11852e9db64d182d6f.png", caption: "Core Ground Connection Point" }
        ]
      },
      {
        name: "Unintentional Core Ground",
        description: "Additional core grounding points creating circulating currents and heating.",
        importance: "Extra ground paths cause eddy currents, generating heat and gases.",
        approach: "Locate additional grounds through testing. Remove or isolate extra connections.",
        severity: "Moderate",
        relatedTests: ["Core Ground Test"],
        relatedMonitoring: [],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/0bf6e90a9fe35ac9434f80dff6a164746ab9b9467927b5249d56c86dd2030877.png", caption: "Core Ground Testing" }
        ]
      }
    ],
    tests: [
      {
        name: "SFRA (Sweep Frequency Response)",
        description: "Compares frequency response of windings to baseline to detect mechanical deformation.",
        importance: "Most sensitive test for detecting winding movement, deformation, and loose components after through-faults.",
        approach: "Inject sweep frequency signal. Compare response to baseline and between phases.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Winding/Core Overheating", "Generation of Bubbles", "PD in Winding Insulation"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/cac814ac95705ed34ddb0b3849775c4f1bee4cc5a217c957fc9fa443aeea6e93.png", caption: "SFRA Response Comparison" }
        ]
      },
      {
        name: "Winding Resistance",
        description: "DC resistance measurement of transformer windings.",
        importance: "Detects broken strands, loose connections, and tap changer contact issues.",
        approach: "Inject DC current and measure voltage drop. Compare phases and to baseline.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Winding/Core Overheating", "Loose Connection with Bushings"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/fa589b6693812a10fc10344f6a5ac9dda5ecf9661513e0ebc9f3abaf652f91a1.png", caption: "Winding Resistance Test Setup" }
        ]
      },
      {
        name: "Insulation Resistance",
        description: "Measures resistance of insulation system at DC voltage.",
        importance: "Basic screening test for gross insulation problems. Affected by moisture and contamination.",
        approach: "Apply DC test voltage. Measure IR after 1 minute and 10 minutes for PI calculation.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Excess Moisture in Insulation", "Generation of Bubbles"],
        images: [
          { url: "https://images.pexels.com/photos/14319099/pexels-photo-14319099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", caption: "Insulation Test Equipment" }
        ]
      },
      {
        name: "Turns Ratio Test",
        description: "Verifies the voltage ratio between windings matches nameplate.",
        importance: "Detects shorted turns and tap changer issues. Fundamental transformer test.",
        approach: "Apply voltage to one winding and measure induced voltage on others. Calculate ratio.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: [],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/fa589b6693812a10fc10344f6a5ac9dda5ecf9661513e0ebc9f3abaf652f91a1.png", caption: "TTR Test Equipment" }
        ]
      },
      {
        name: "Core Ground Test",
        description: "Verifies core is grounded at one point only with proper continuity.",
        importance: "Essential for safe operation. Multiple grounds or open grounds both cause problems.",
        approach: "Measure resistance from core to ground. Check for unintentional grounds.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Loss of Core Ground", "Unintentional Core Ground"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/0bf6e90a9fe35ac9434f80dff6a164746ab9b9467927b5249d56c86dd2030877.png", caption: "Core Ground Test Equipment" }
        ]
      }
    ],
    monitoring: [
      {
        name: "Fiber Optic Temp Sensors",
        description: "Direct winding hot spot temperature measurement using fiber optic probes.",
        importance: "Only way to directly measure actual winding temperature. Critical for dynamic loading.",
        approach: "Install probes during manufacturing or retrofit. Use for protection and loading decisions.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Winding/Core Overheating", "Excess Moisture in Insulation", "Loose Connection with Bushings"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/f979df3143ae6e2bb1d78cf12f74a6dbc4c0ebc5ffd4a5693a10fbb05b471ea1.png", caption: "Fiber Optic Temperature Probe" }
        ]
      },
      {
        name: "Online PD Monitoring",
        description: "Continuous monitoring for partial discharge activity in windings.",
        importance: "Early warning of developing insulation faults. Enables planned intervention.",
        approach: "Install UHF sensors or bushing tap monitors. Analyze PD patterns for fault type.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["PD in Winding Insulation"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/9c3ef80b5950704fbeda8c64fb157b724733ac0d589c86d886e9c007b4ef7d21.png", caption: "PD Monitoring System" }
        ]
      },
      {
        name: "Winding Hot Spot Monitors",
        description: "Calculated or measured winding hot spot temperature based on load and ambient.",
        importance: "Prevents overheating during high loads. Enables dynamic rating.",
        approach: "Use thermal model or direct measurement. Set alarms and protection limits.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Winding/Core Overheating", "Generation of Bubbles"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/558a66bfa52a5349f80f8bda20f3efea19356363cc707d45b349ca9b737074c8.png", caption: "Temperature Monitoring Display" }
        ]
      }
    ]
  },
  {
    id: "5",
    number: 5,
    name: "On Load Tap Changer (OLTC)",
    color: "#8B5CF6",
    failureModes: [
      {
        name: "OLTC End of Life",
        description: "Tap changer mechanism reaching end of service life with accumulated wear on all components.",
        importance: "Increases risk of multiple failure modes. Planned replacement prevents failures.",
        approach: "Assess condition through testing and inspection. Plan overhaul or replacement.",
        severity: "Moderate",
        relatedTests: ["Tap Changer Timing Test", "Dynamic Resistance"],
        relatedMonitoring: ["OLTC Monitors", "Tap Position Trending"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/04d99db5f809257f7e64c06c81abdd1db20ff669d4ff82713c37b634f21ee66c.png", caption: "OLTC Mechanism Internals" }
        ]
      },
      {
        name: "OLTC Contact Coking",
        description: "Carbon buildup on selector contacts from arcing and oil decomposition.",
        importance: "Increases contact resistance and heating. Can lead to contact welding or failure.",
        approach: "Clean or replace contacts during maintenance. Use OLTC-specific oil if separate compartment.",
        severity: "Moderate",
        relatedTests: ["Contact Resistance", "OLTC Oil Analysis"],
        relatedMonitoring: ["OLTC Monitors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/04d99db5f809257f7e64c06c81abdd1db20ff669d4ff82713c37b634f21ee66c.png", caption: "OLTC Contact Assembly" }
        ]
      },
      {
        name: "OLTC Multiple Tap Movement",
        description: "Unintended multiple tap changes during a single operation command.",
        importance: "Causes excessive wear and contact damage. May indicate control or mechanism issues.",
        approach: "Check control circuit and limit switches. Verify motor operation and stopping.",
        severity: "Moderate",
        relatedTests: ["Tap Changer Timing Test"],
        relatedMonitoring: ["Motor Current Signature Analysis", "Tap Position Trending"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/9c92dd4513ff619ea213d1893ac89c620247ed9ad1846bf9bf9c086d162042c9.png", caption: "Timing Test Waveform Analysis" }
        ]
      },
      {
        name: "OLTC Excess Arcing",
        description: "Abnormally high arcing during tap changes due to contact wear or timing issues.",
        importance: "Accelerates oil degradation and contact erosion. Generates gases detectable in DGA.",
        approach: "Analyze DGA for arcing gases. Inspect and replace worn contacts. Check timing.",
        severity: "Critical",
        relatedTests: ["OLTC Oil Analysis", "Dynamic Resistance"],
        relatedMonitoring: ["OLTC Monitors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/147f2abd5ed8a45d7f949e86f9a976bff120c3283d1dc712a853ae93fb948752.png", caption: "DGA Analysis for Arcing" }
        ]
      },
      {
        name: "OLTC Motor Drive Issues",
        description: "Problems with the motor mechanism that drives tap changes including wear, overheating, or electrical faults.",
        importance: "Can prevent voltage regulation or cause incomplete tap changes. Safety risk.",
        approach: "Check motor current signature. Inspect drive mechanism for wear. Lubricate as needed.",
        severity: "Moderate",
        relatedTests: ["Tap Changer Timing Test"],
        relatedMonitoring: ["Motor Current Signature Analysis"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/575f8fdf883a39164ed97014fe239ff2c73ce7d618297d283d7c86cb224ac6ac.png", caption: "Motor Condition Analysis" }
        ]
      },
      {
        name: "OLTC Oil - Weak Insulation",
        description: "Degraded oil in OLTC compartment with reduced dielectric strength.",
        importance: "Increases risk of internal flashover. OLTC oil degrades faster than main tank oil.",
        approach: "Test oil regularly. Filter or replace when values exceed limits.",
        severity: "Moderate",
        relatedTests: ["OLTC Oil Analysis"],
        relatedMonitoring: ["OLTC Monitors"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/3fc2ee4952f5d2185febe14136e64c08c55df19756dae7deaa73bb42f016cde6.png", caption: "Oil Dielectric Strength Test" }
        ]
      }
    ],
    tests: [
      {
        name: "Tap Changer Timing Test",
        description: "Measures the timing of tap change operations including transition time and contact sequencing.",
        importance: "Verifies mechanism operates within specification. Detects wear and misadjustment.",
        approach: "Record current and position during tap operations. Analyze timing against manufacturer spec.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["OLTC End of Life", "OLTC Multiple Tap Movement", "OLTC Motor Drive Issues"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/9c92dd4513ff619ea213d1893ac89c620247ed9ad1846bf9bf9c086d162042c9.png", caption: "Timing Test Equipment" }
        ]
      },
      {
        name: "Contact Resistance",
        description: "Measurement of resistance through tap changer contacts at each position.",
        importance: "High resistance indicates contact wear, coking, or poor pressure. Critical measurement.",
        approach: "Inject DC current through each tap position. Measure and compare to baseline.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["OLTC Contact Coking"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/fa589b6693812a10fc10344f6a5ac9dda5ecf9661513e0ebc9f3abaf652f91a1.png", caption: "Contact Resistance Measurement" }
        ]
      },
      {
        name: "OLTC Oil Analysis",
        description: "Testing of oil specifically from the tap changer compartment for gases and quality.",
        importance: "OLTC oil shows different characteristics than main tank. Reveals arcing and contact issues.",
        approach: "Sample from OLTC compartment separately. Analyze DGA and oil quality.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["OLTC Contact Coking", "OLTC Excess Arcing", "OLTC Oil - Weak Insulation"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/f317fac610f027f223b15c181d3791a2cb66823afbfde0d0daf79c59bbe0f1da.png", caption: "Oil Sampling Equipment" },
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/147f2abd5ed8a45d7f949e86f9a976bff120c3283d1dc712a853ae93fb948752.png", caption: "Gas Analysis Results" }
        ]
      },
      {
        name: "Dynamic Resistance",
        description: "Continuous resistance measurement during tap change operations.",
        importance: "Reveals contact quality and transition resistor operation. Advanced diagnostic technique.",
        approach: "Inject current and measure resistance continuously while changing taps.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["OLTC End of Life", "OLTC Excess Arcing"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/9c92dd4513ff619ea213d1893ac89c620247ed9ad1846bf9bf9c086d162042c9.png", caption: "Dynamic Resistance Waveform" }
        ]
      }
    ],
    monitoring: [
      {
        name: "OLTC Monitors",
        description: "Dedicated monitoring systems for tap changer health including oil, position, and operations.",
        importance: "Most critical monitoring point on many transformers. OLTC causes majority of failures.",
        approach: "Install comprehensive monitor. Track operations count, oil condition, and timing trends.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["OLTC End of Life", "OLTC Contact Coking", "OLTC Excess Arcing", "OLTC Oil - Weak Insulation"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/04d99db5f809257f7e64c06c81abdd1db20ff669d4ff82713c37b634f21ee66c.png", caption: "OLTC Monitoring System" }
        ]
      },
      {
        name: "Motor Current Signature Analysis",
        description: "Analysis of OLTC motor current pattern during tap changes.",
        importance: "Reveals motor and mechanism condition. Non-invasive diagnostic technique.",
        approach: "Record motor current during operations. Analyze waveform for anomalies.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["OLTC Multiple Tap Movement", "OLTC Motor Drive Issues"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/575f8fdf883a39164ed97014fe239ff2c73ce7d618297d283d7c86cb224ac6ac.png", caption: "Motor Current Analysis" }
        ]
      },
      {
        name: "Tap Position Trending",
        description: "Long-term tracking of tap position and frequency of changes.",
        importance: "Reveals operating patterns. Helps plan maintenance based on actual usage.",
        approach: "Log tap position continuously. Analyze patterns and count operations.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["OLTC End of Life", "OLTC Multiple Tap Movement"],
        images: [
          { url: "https://static.prod-images.emergentagent.com/jobs/5dae0428-c90f-47d3-96c5-04b06478a28c/images/6994e668904e78fa5fa8e35a84078204863e91d115eab40936950c9c72add62a.png", caption: "Tap Position Display & Trending" }
        ]
      }
    ]
  }
];

// Helper function to get simple arrays for backward compatibility
export const getSimpleTransformerComponents = () => {
  return TRANSFORMER_DATA.map(comp => ({
    id: comp.id,
    number: comp.number,
    name: comp.name,
    color: comp.color,
    failureModes: comp.failureModes.map(f => f.name),
    tests: comp.tests.map(t => t.name),
    monitoring: comp.monitoring.map(m => m.name)
  }));
};
